/**
 * Sends the user to sign-in when their session ends while they are using the
 * app, instead of leaving every panel showing its own request error.
 *
 * The route guards only look at the session on navigation, and they read it
 * from the cache. A session that expires, or is revoked from another device,
 * while someone sits on a page therefore surfaced as a scatter of failed
 * requests and toasts, with nothing telling them to sign in again.
 */
import type { QueryClient } from "@tanstack/react-query";

import { apiFetch, apiUrl } from "./api";
import { clearAccountCache, markSignedOut, type SessionPayload, sessionQuery } from "./session";
import { onUnauthorized } from "./unauthorized";

type SessionExpiryOptions = {
  /** The URL the user is on, carried through sign-in so they land back on it. */
  currentHref: () => string;
  /** Navigates to this app's sign-in route with `?redirect=`. */
  goToSignIn: (redirect: string) => Promise<void>;
  /** Tells the user why they are looking at the sign-in form. */
  onExpired?: () => void;
};

/**
 * Whether the backend still recognises the session: `true`/`false`, or `null`
 * when it cannot tell (offline, a 5xx). Deliberately not `fetchSession`, which
 * folds "unreachable" into "signed out" — a network blip must not sign anyone
 * out.
 */
async function sessionStillValid(): Promise<SessionPayload | false | null> {
  try {
    const res = await apiFetch(apiUrl("/api/auth/get-session"), { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as SessionPayload | null;
    return data?.user ? data : false;
  } catch {
    return null;
  }
}

/**
 * Wires 401 responses to the sign-in redirect. Call once from the app entry.
 *
 * A 401 alone is not trusted: it is confirmed against the session endpoint, so
 * a single endpoint that answers 401 for its own reasons cannot sign anyone
 * out. Concurrent 401s — a page's worth of queries failing together — share
 * one check and one redirect. Nothing happens unless the cache believes the
 * visitor is signed in, which also keeps public pages out of it.
 */
export function installSessionExpiryHandler(
  queryClient: QueryClient,
  { currentHref, goToSignIn, onExpired }: SessionExpiryOptions,
): () => void {
  let checking = false;

  return onUnauthorized(() => {
    if (checking || !queryClient.getQueryData(sessionQuery.queryKey)) return;
    checking = true;

    void (async () => {
      const session = await sessionStillValid();
      if (session === null) return;
      if (session) {
        queryClient.setQueryData(sessionQuery.queryKey, session);
        return;
      }

      const redirect = currentHref();
      // Same order as a manual sign-out: record it, leave, then clear, so the
      // page being left doesn't re-render as skeletons on the way out.
      markSignedOut(queryClient);
      onExpired?.();
      await goToSignIn(redirect);
      clearAccountCache(queryClient);
      markSignedOut(queryClient);
    })().finally(() => {
      checking = false;
    });
  });
}
